import {
  formatMinorAmount,
  MBA_INSTALLMENT_COUNT,
  MBA_INSTALLMENT_ORIGINAL_PRICE_PER_MONTH_CENTS,
  MBA_INSTALLMENT_PRICE_CENTS,
  MBA_ONE_TIME_ORIGINAL_PRICE_CENTS,
  MBA_ONE_TIME_PRICE_CENTS,
} from '@repo/common';
import { BillingPlanKind } from '@repo/frontend-shared';

import { formatBillingDate } from './billing-format';

interface BillingPlanMoneyLike {
  amountMinor: number;
  currency: string;
}

export function formatBillingPlanAmount(money: BillingPlanMoneyLike): string {
  return formatMinorAmount(money.amountMinor, money.currency);
}

/** "N of 6 payments completed", the progress line under the plan name. */
export function describeInstallmentProgress(installmentsPaid: number, installmentCount: number): string {
  return `${installmentsPaid} of ${installmentCount} payments completed`;
}

/** "Plan total: $1,234" — the supporting line under the total-paid fact. */
export function describePlanTotal(planTotal: BillingPlanMoneyLike): string {
  return `Plan total: ${formatBillingPlanAmount(planTotal)}`;
}

/** "Final payment due: 5 January 2027" — absent once the plan has no next payment. */
export function describeFinalPaymentDue(finalPaymentDueAt: string | undefined): string | undefined {
  return finalPaymentDueAt ? `Final payment due: ${formatBillingDate(finalPaymentDueAt)}` : undefined;
}

/** "Due: 5 September 2026" — absent once the plan has no next payment. */
export function describeNextPaymentDue(nextPaymentDueAt: string | undefined): string | undefined {
  return nextPaymentDueAt ? `Due: ${formatBillingDate(nextPaymentDueAt)}` : undefined;
}

export function isInstallmentPlanKind(kind: BillingPlanKind): boolean {
  return kind === BillingPlanKind.INSTALLMENT;
}

/** The plan's name as every frame titles it: "One-time payment" or "6-month payment". */
export function describePlanKind(kind: BillingPlanKind): string {
  return isInstallmentPlanKind(kind) ? `${MBA_INSTALLMENT_COUNT}-month payment` : 'One-time payment';
}

/**
 * The founder discount this plan was actually sold at, as a whole percentage.
 *
 * Derived rather than stored: nothing records a discount against a purchase,
 * but the founding price and the list price are both constants, and a plan
 * bought at the founding price is exactly the one the line is about. Comparing
 * the committed total against what the list price would have been is what
 * keeps the line honest if pricing ever changes — a plan sold at list price
 * shows no discount rather than claiming one.
 */
export function resolveFounderDiscountPercent(
  kind: BillingPlanKind,
  planTotal: BillingPlanMoneyLike,
): number | undefined {
  const installments = isInstallmentPlanKind(kind) ? MBA_INSTALLMENT_COUNT : 1;
  const founderTotal = isInstallmentPlanKind(kind)
    ? MBA_INSTALLMENT_PRICE_CENTS * installments
    : MBA_ONE_TIME_PRICE_CENTS;
  const listTotal = isInstallmentPlanKind(kind)
    ? MBA_INSTALLMENT_ORIGINAL_PRICE_PER_MONTH_CENTS * installments
    : MBA_ONE_TIME_ORIGINAL_PRICE_CENTS;

  if (planTotal.amountMinor !== founderTotal || listTotal <= founderTotal) {
    return undefined;
  }
  return Math.round(((listTotal - founderTotal) / listTotal) * 100);
}
